import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'campaign-step-schedule' }

import { computed, type PropType } from 'vue'
import { TimePicker } from 'element-ui'
import moment from 'moment-timezone'
import { pickerDateFormat as getPickerDateFormat, pickerTimeFormat as getPickerTimeFormat, formatGroupDate, formatGroupTime } from '@/plugins/dates'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  // true for an ongoing campaign: daily time plus an optional stop date
  ongoing: { type: Boolean, default: false },
  sendMode: { type: String, default: 'now' },
  scheduleDate: { type: String, default: '' }, // yyyy-MM-dd (group wall date)
  scheduleTime: { type: String, default: '' }, // HH:mm (group wall time)
  endsOn: { type: String, default: '' },       // yyyy-MM-dd, ongoing only
  conditionDays: { type: Number, default: 7 }, // ongoing only: days before the birthday
  // ongoing only: which daily condition, and the visit condition's settings
  conditionType: { type: String as PropType<'birthday_in_days' | 'days_since_last_visit'>, default: 'birthday_in_days' },
  // No defaults: a cleared field must stay empty (the parent holds undefined),
  // and el-input-number clamps null up to its minimum, so null never reaches it.
  lapseDays: { type: Number as PropType<number | undefined>, default: undefined },
  dailyLimit: { type: Number as PropType<number | null | undefined>, default: undefined },
  skipPreviouslyEmailed: { type: Boolean, default: true }
},
  emits: [
  'update:send-mode', 'update:schedule-date', 'update:schedule-time', 'update:ends-on', 'update:condition-days',
  'update:condition-type', 'update:lapse-days', 'update:daily-limit', 'update:skip-previously-emailed'
],
  setup(__props) {

const props = __props





const conditionDaysLabel = computed(() => {
  const d = Number.isInteger(props.conditionDays) ? props.conditionDays : 0
  return d === 0 ? '0 days' : d === 1 ? '1 day' : `${d} days`
})
const lapseDaysLabel = computed(() => {
  const d = Number.isInteger(props.lapseDays) ? props.lapseDays : 0
  return d === 1 ? '1 day' : `${d} days`
})
const conditionHint = computed(() => props.conditionType === 'days_since_last_visit'
  ? `Runs every day at this time in your group's timezone and emails the customers whose last visit is more than ${lapseDaysLabel.value} old, longest ago first, one email per customer until they visit again. The first run reaches everyone who is already past ${lapseDaysLabel.value}; set a per day limit to spread it out. Customers who have never visited are not included. Leave Stop after empty to keep it running until you stop it.`
  : `Runs every day at this time in your group's timezone and emails the customers whose birthday is ${conditionDaysLabel.value} away that day, one email each. Customers without a birthday on file are never included. Leave Stop after empty to keep it running until you stop it.`)

const group = JSON.parse(localStorage.getItem('group') || '{}')
const tzName = group.tz_name || 'UTC'
const firstDayOfWeek = Number(group.first_day_of_week) || 1
// The picker helpers take the group's stored format and translate it to
// element-ui tokens; called bare they silently fall back to US defaults.
// dddd prefixes the weekday name when the group shows day of week.
const pickerDateFormat = (group.show_day_of_week ? 'dddd, ' : '') + getPickerDateFormat(group.date_format)
const pickerTimeFormat = getPickerTimeFormat(group.time_format)

function isPastDate(date: Date) {
  const startOfToday = moment.tz(tzName).startOf('day')
  return moment(date).isBefore(startOfToday)
}

const scheduledMoment = computed(() => {
  if (!props.scheduleDate || !props.scheduleTime) return null
  return moment.tz(`${props.scheduleDate} ${props.scheduleTime}`, 'YYYY-MM-DD HH:mm', tzName)
})

const resolvedDisplay = computed(() => {
  const m = scheduledMoment.value
  if (!m || !m.isValid() || !m.isAfter(moment())) return ''
  const iso = m.toISOString()
  return `${formatGroupDate(iso)}, ${formatGroupTime(iso)}`
})

const quietHoursWarning = computed(() => {
  if ((!props.ongoing && props.sendMode !== 'schedule') || !props.scheduleTime) return ''
  const hour = Number(props.scheduleTime.split(':')[0])
  if (hour < 8 || hour >= 20) {
    return 'This send time falls outside typical waking hours. Marketing emails land best between 8:00 and 20:00 in your local timezone.'
  }
  return ''
})

const pastError = computed(() => {
  const m = scheduledMoment.value
  if (m && m.isValid() && !m.isAfter(moment())) {
    return 'The scheduled time must be in the future.'
  }
  return ''
})

return { __sfc: true,props, conditionDaysLabel, lapseDaysLabel, conditionHint, group, tzName, firstDayOfWeek, pickerDateFormat, pickerTimeFormat, isPastDate, scheduledMoment, resolvedDisplay, quietHoursWarning, pastError, TimePicker }
}

})